import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import AdminLayout from '../../layouts/AdminLayout';

const Documentation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDocumentation();
    fetchCategories();
  }, []);

  const fetchDocumentation = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');
    
    if (!token) {
      setError('Usuário não autenticado. Faça login para visualizar este conteúdo.');
      setLoading(false);
      return;
    }

    try {
      // Primeiro tentamos buscar da nova API de conhecimento
      const response = await axios.get(`/api/knowledge/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setItems(response.data);
      setLoading(false);
    } catch (knowledgeError) {
      console.error('Erro ao buscar da API de conhecimento:', knowledgeError);

      try {
        // Caso falhe, tentamos a API de documentação antiga
        const response = await axios.get(`/api/documentation`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setItems(response.data);
        setLoading(false);
      } catch (docError) {
        console.error('Erro ao buscar da API de documentação:', docError);
        setError('Não foi possível obter os dados da documentação. Tente novamente mais tarde.');
        setLoading(false);
        toast.error('Erro ao carregar dados da documentação');
      }
    }
  };

  const fetchCategories = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      // Primeiro tentar buscar categorias da API de conhecimento
      const response = await axios.get(`/api/knowledge/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.categories && response.data.topics) {
        setCategories(response.data.categories);
        setTopics(response.data.topics);
      } else {
        // Se não retornar no formato esperado, criar categorias e tópicos a partir dos itens
        deriveFiltersFromItems();
      }
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
      // Em caso de erro, criar categorias e tópicos a partir dos itens
      deriveFiltersFromItems();
    }
  };

  const deriveFiltersFromItems = () => {
    if (items.length === 0) return;

    const uniqueCategories = [...new Set(items.map(item => item.category).filter(Boolean))];
    const uniqueTopics = [...new Set(items.map(item => item.topic).filter(Boolean))];
    
    setCategories(uniqueCategories);
    setTopics(uniqueTopics);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedTopic('');
    setSearchTerm('');
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = searchTerm === '' || 
      (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === '' || item.category === selectedCategory;
    const matchesTopic = selectedTopic === '' || item.topic === selectedTopic;
    
    return matchesSearch && matchesCategory && matchesTopic;
  });

  return (
    <AdminLayout>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">
                Documentação
              </h2>
              <div className="text-muted mt-1">
                {loading ? (
                  'Carregando itens...'
                ) : error ? (
                  'Erro ao carregar documentação'
                ) : (
                  `${filteredItems.length} item(s) encontrado(s)`
                )}
              </div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button className="btn btn-primary d-none d-sm-inline-block" onClick={() => navigate('/admin/documentation/new')}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M12 5l0 14"></path>
                    <path d="M5 12l14 0"></path>
                  </svg>
                  Novo Item
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="mt-2">Carregando documentação...</p>
            </div>
          ) : error ? (
            <div className="empty">
              <div className="empty-img">
                <img src="/static/illustrations/undraw_quitting_time_dm8t.svg" height="128" alt="Erro" />
              </div>
              <p className="empty-title">Erro ao carregar documentação</p>
              <p className="empty-subtitle text-muted">
                {error}
              </p>
              <div className="empty-action">
                <button className="btn btn-primary" onClick={fetchDocumentation}>
                  Tentar Novamente
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="card mb-3">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label">Pesquisar</label>
                        <div className="input-icon mb-3">
                          <input 
                            type="text" 
                            className="form-control" 
                            placeholder="Título ou descrição..." 
                            value={searchTerm}
                            onChange={handleSearch}
                          />
                          <span className="input-icon-addon">
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                              <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path>
                              <path d="M21 21l-6 -6"></path>
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label className="form-label">Categoria</label>
                        <select 
                          className="form-select" 
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                          <option value="">Todas as categorias</option>
                          {categories.map((category, index) => (
                            <option key={index} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label className="form-label">Tópico</label>
                        <select 
                          className="form-select" 
                          value={selectedTopic}
                          onChange={(e) => setSelectedTopic(e.target.value)}
                        >
                          <option value="">Todos os tópicos</option>
                          {topics.map((topic, index) => (
                            <option key={index} value={topic}>{topic}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <button className="btn btn-outline-secondary w-100" onClick={clearFilters}>
                        Limpar Filtros
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {filteredItems.length === 0 ? (
                <div className="empty">
                  <div className="empty-img">
                    <img src="/static/illustrations/undraw_searching_re_3ra9.svg" height="128" alt="" />
                  </div>
                  <p className="empty-title">Nenhum item encontrado</p>
                  <p className="empty-subtitle text-muted">
                    Tente ajustar os filtros ou termos de pesquisa.
                  </p>
                  <div className="empty-action">
                    <button className="btn btn-primary" onClick={clearFilters}>
                      Limpar Filtros
                    </button>
                  </div>
                </div>
              ) : (
                <div className="row row-cards">
                  {filteredItems.map((item) => (
                    <div className="col-md-6 col-xl-4" key={item.id}>
                      <div className="card">
                        <div className="card-header">
                          <h3 className="card-title">{item.title}</h3>
                          <div className="card-actions">
                            <span className={`badge bg-${getCategoryColor(item.category)}-lt me-1`}>
                              {item.category || 'Sem categoria'}
                            </span>
                            <span className={`badge bg-${getTopicColor(item.topic)}-lt`}>
                              {item.topic || 'Sem tópico'}
                            </span>
                          </div>
                        </div>
                        <div className="card-body">
                          <p className="text-muted">{item.description}</p>
                        </div>
                        <div className="card-footer">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <span className="text-muted">Data: </span>
                              <strong>{formatDate(item.date)}</strong>
                            </div>
                            <div>
                              <Link to={`/admin/documentation/${item.id}`} className="btn btn-sm btn-outline-primary me-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                  <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                  <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                </svg>
                                Ver
                              </Link>
                              <Link to={`/admin/documentation/edit/${item.id}`} className="btn btn-sm btn-outline-secondary">
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-edit" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                  <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"></path>
                                  <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"></path>
                                  <path d="M16 5l3 3"></path>
                                </svg>
                                Editar
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return 'Data não disponível';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const getCategoryColor = (category) => {
  if (!category) return 'secondary';
  
  switch (category.toLowerCase()) {
    case 'nova funcionalidade':
      return 'green';
    case 'correção de bug':
      return 'red';
    case 'melhoria':
      return 'blue';
    case 'documentação':
      return 'purple';
    case 'refatoração':
      return 'orange';
    default:
      return 'secondary';
  }
};

const getTopicColor = (topic) => {
  if (!topic) return 'secondary';
  
  switch (topic.toLowerCase()) {
    case 'frontend':
      return 'indigo';
    case 'backend':
      return 'azure';
    case 'api':
      return 'cyan';
    case 'database':
      return 'teal';
    case 'ui/ux':
      return 'pink';
    case 'infraestrutura':
      return 'orange';
    case 'segurança':
      return 'red';
    case 'performance':
      return 'lime';
    case 'cenários e simulações':
      return 'purple';
    default:
      return 'secondary';
  }
};

export default Documentation; 